import { FC, memo, useCallback, useMemo, useRef, useState } from 'react'

import { EventRecording } from '@crew/apis/eventRecordingTranscribe/models/getEventRecordingTranscribes/response'
import { Region, SettingKeyType } from '@crew/enums/app'
import { JsonDateFormat } from '@crew/enums/system'
import dayjs from '@crew/modules'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'
import { useGetEventRecordingsAudioUrlQuery } from '@crew/apis/project/projectApis'

import { EventRecordingTranscribeItem } from './components/eventRecordingTranscribeItem/eventRecordingTranscribeItem'
import {
  CrewSoundPlayer,
  ICrewSoundPlayer,
  SoundStatus,
} from 'components/elements/crewSoundPlayer/crewSoundPlayer'
import { RecordingType } from '@crew/enums/domain'

type Props = {
  eventRecording: EventRecording
}

export const EventDetailMeetingMinutesPanelTranscriptionListItem: FC<Props> =
  memo((props) => {
    const { t } = useTranslation()

    // ユーザー設定からデフォルトのユーザープロファイル地域を取得
    const defaultUserProfileRegion = useUserSetting(
      SettingKeyType.UserProfileRegion,
      Region.Japan.value
    )

    // 音声ファイルのURLを取得
    const { data: getEventRecordingsAudioUrlResult } =
      useGetEventRecordingsAudioUrlQuery({
        eventRecordingId: props.eventRecording.id,
      })
    const audioUrl = getEventRecordingsAudioUrlResult?.url //単純な代入なのでuseMemoしない

    // 音声の再生状態
    const [soundStatus, setSoundStatus] = useState<SoundStatus | null>(null)

    // 音声プレイヤーのRef
    const soundPlayerRef = useRef<ICrewSoundPlayer>(null)

    // 編集状態の管理用State
    const [editingTranscribeId, setEditingTranscribeId] = useState<
      string | null
    >(null)

    // この録画中での発言時刻の最大時間を取得する
    const maxSpeechTime = useMemo(
      () =>
        props.eventRecording.eventRecordingTranscribes.length > 0
          ? props.eventRecording.eventRecordingTranscribes.reduce(
              (max, transcribe) =>
                Math.max(max, transcribe.speechTimestamp || 0),
              0
            )
          : undefined,
      [props.eventRecording.eventRecordingTranscribes]
    )

    // 録画のrender
    // CrewWorksで録画したものは「録画日時」、アップロードした録画ファイルは「ファイル名」を表示
    const renderMeetingRecording = useCallback(
      (
        recordingType: RecordingType,
        startDatetime: string | null,
        endDatetime: string | null,
        fileName: string | null
      ) => {
        if (recordingType === RecordingType.Internal) {
          /* CrewWorks上で録画の場合は録画日時を表示 */

          // 録画開始日時がnullのケースはありえないため、何も表示しない
          if (startDatetime === null) {
            return null
          }

          // 終了日時がnullの場合は開始日時のみ表示
          // →録画終了直後は終了日時が登録されていない状態になるため
          if (!endDatetime) {
            return t('format.datetime', {
              value: startDatetime,
              timeZone: defaultUserProfileRegion,
            })
          }

          // 開始日と終了日が同じか
          const isSameDay =
            dayjs(startDatetime).format(JsonDateFormat.YYYYMMDD) ===
            dayjs(endDatetime).format(JsonDateFormat.YYYYMMDD)

          return (
            <>
              <time>
                {t('format.datetime', {
                  value: startDatetime,
                  timeZone: defaultUserProfileRegion,
                })}
              </time>
              <time> - </time>
              <time>
                {t(isSameDay ? 'format.shorttime' : 'format.datetime', {
                  value: endDatetime,
                  timeZone: defaultUserProfileRegion,
                })}
              </time>
            </>
          )
        } else if (recordingType === RecordingType.External) {
          /* アップロードした録画ファイルの場合はファイル名を表示 */

          // ファイル名がnullの場合は何も表示しない
          if (fileName === null) {
            return null
          }

          return <span>{fileName}</span>
        }
      },
      [defaultUserProfileRegion, t]
    )

    // 音声の再生
    const audioPlay = useCallback<ICrewSoundPlayer['play']>((...params) => {
      soundPlayerRef.current?.play(...params)
    }, [])

    // 音声の停止
    const audioStop = useCallback(() => {
      soundPlayerRef.current?.stop()
    }, [])

    return (
      <div key={props.eventRecording.id} className="flex flex-col gap-y-1">
        {/* 録画日時 */}
        <div className="font-bold py-1 px-1.5 crew-bg-gray-1 rounded">
          {renderMeetingRecording(
            props.eventRecording.recordingType,
            props.eventRecording.startDatetime,
            props.eventRecording.endDatetime,
            props.eventRecording.fileName
          )}
        </div>

        {props.eventRecording.eventRecordingTranscribes.map(
          (eventRecordingTranscribe, i, arr) => (
            <EventRecordingTranscribeItem
              key={eventRecordingTranscribe.id}
              maxSpeechTime={maxSpeechTime}
              editingTranscribeId={editingTranscribeId}
              setEditingTranscribeId={setEditingTranscribeId}
              eventRecordingTranscribe={eventRecordingTranscribe}
              nextSpeechTimestamp={
                arr.length > i + 1 ? arr[i + 1].speechTimestamp : null
              }
              audioState={
                !soundStatus || soundStatus.state === 'loading'
                  ? 'disabled'
                  : soundStatus.state
              }
              audioCurrentRequester={soundStatus?.currentRequester || null}
              audioPlay={audioPlay}
              audioStop={audioStop}
            />
          )
        )}

        {audioUrl && (
          <CrewSoundPlayer
            ref={soundPlayerRef}
            url={audioUrl}
            onChangeStatus={setSoundStatus}
          />
        )}
      </div>
    )
  })
